import { useState, useEffect } from 'react'
import Section from './_Section'
import ButtonEl from '../helpers/ctas/Button'
import Image from '../helpers/Image'
import TitleBlock from '../helpers/TitleBlock'
import { transformPaddingToTailwind } from '../../helpers/style'
import { useMemberSession } from '../../helpers/memberSession'

function InformationBanner(props) {
  const { isAuthenticated, loaded } = useMemberSession()
  const [textAlignment, setTextAlignment] = useState({
    flex: 'center',
    align: 'center',
  })
  const componentPadding = transformPaddingToTailwind(props.componentPadding)

  // Registration banner variant: split layout (text left / CTAs right on desktop)
  // and hidden for logged-in members per the tech spec.
  const isRegistration = props.registrationMode

  useEffect(() => {
    setTextAlignment((prevState) => {
      const newState = { ...prevState }
      // The registration variant is always left-aligned regardless of the content alignment setting.
      const alignment = isRegistration ? 'left' : (props.contentAlignment?.toLowerCase() ?? 'center')
      if (newState.align !== alignment) {
        newState.flex = alignment === 'center' ? 'center' : 'start'
        newState.align = alignment === 'center' ? 'center' : 'left'
      }
      return newState
    })
  }, [])

  // Use content tab images if they exist, otherwise fall back to Component Configuration background images
  const desktopBackgroundImage = props.desktopImage || props.desktopBackgroundImage
  const mobileBackgroundImage = props.mobileImage || props.mobileBackgroundImage

  // Check if we have a background (color or image)
  const hasBorder = props.hasBorder

  // Hide the registration banner once we know the visitor is signed in. We render
  // by default (and while the session is still loading) so anonymous visitors —
  // the banner's audience — never see a flash of empty space waiting on the fetch.
  if (isRegistration && loaded && isAuthenticated) {
    return null
  }

  const ctaButtons =
    props.links && props.links.length != 0
      ? props.links.map((ctaLinkItem, i) => (
          <ButtonEl
            key={i}
            link={ctaLinkItem.link}
            icon={ctaLinkItem.linkIconClass}
            position={ctaLinkItem.linkPosition}
            theme={ctaLinkItem.linkType.toLowerCase()}
            className={`${!props.backgroundColor && ctaLinkItem.linkType.toLowerCase() === 'text' ? 'text-(--color-secondary)' : ''}`}
          />
        ))
      : null

  return (
    <Section
      type="informationBanner"
      outerClass={componentPadding}
      sectionTitle={false}
      innerClass={`px-7 text-${textAlignment.align}`}
      {...props}
      mobileBackgroundImage={mobileBackgroundImage}
      desktopBackgroundImage={desktopBackgroundImage}
    >
      <div className={isRegistration ? 'lg:py-12 lg:px-16' : 'lg:py-24 lg:px-16'}>
        {/* Top Divider: Only renders if NO background is present */}
        {hasBorder && <div className="border-t border-(--color-border-tertiary) -mx-7 mb-7" />}

        {isRegistration ? (
          <div className="registration-banner flex flex-col gap-7 text-left md:flex-row md:items-center md:justify-between md:gap-12">
            <div className="md:flex-1">
              <Image {...props.image} className="mb-7" />
              {(props.title || props.description) && <TitleBlock {...props} />}
            </div>
            {ctaButtons && (
              <div className="flex flex-col gap-4 sm:flex-row md:shrink-0 md:items-center md:justify-end">
                {ctaButtons}
              </div>
            )}
          </div>
        ) : (
          <>
            <Image {...props.image} className="mx-auto mb-7" />
            {(props.title || props.description) && <TitleBlock {...props} />}

            {ctaButtons && (
              <div className={`flex flex-col md:flex-row justify-${textAlignment.flex} gap-4 md:gap-8 mt-10`}>
                {ctaButtons}
              </div>
            )}
          </>
        )}

        {/* Bottom Divider: Only renders if NO background is present */}
        {hasBorder && <div className="border-t border-(--color-border-tertiary) -mx-7 mt-10" />}
      </div>
    </Section>
  )
}

export default InformationBanner
